import { Component, EventEmitter, Output, Input, OnInit, ChangeDetectorRef, inject, DoCheck } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { ChatMessageType } from '../main-layout.model';
import { SESSION_MERGE_STATUS } from 'src/app/base/companion-constants';

export interface SessionQuestion {
  id: string;
  type: 'check-boxes' | 'radio-buttons' | 'textarea' | 'chips' | 'slider' | 'yes-no';
  text: string;
  options?: { id: string; text: string }[]; // for multiple, single, chip, yes-no
  config?: {
    hideOthers?: string;
    startRange?: string;
    endRange?: string;
    interval?: string;
    labels?: {
      low: string;
      high: string;
    };
  };
}

@Component({
  selector: 'rt-qna-question-card',
  standalone: true,
  imports: [CommonModule, FormsModule],
  templateUrl: './qna-question-card.component.html',
  styleUrls: ['./qna-question-card.component.scss']
})
export class QnaQuestionCardComponent implements OnInit, DoCheck {
  private cdr = inject(ChangeDetectorRef);

  readonly ChatMessageType = ChatMessageType;

  private _chatMessage: any;
  @Input() set chatMessage(msg: any) {
    this._chatMessage = msg;
    if (msg) {
      const payload = msg.content || {};
      this.questions = payload.questions || msg.questions || [];
      this.config = payload.config || msg.config;

      this.setCardHeader();
      this.initializeAnswers(); // Always re-initialize map to exact new questions!
      this.cdr.detectChanges(); // Ensure DOM immediately catches up
    }
  }
  get chatMessage(): any {
    return this._chatMessage;
  }

  private _appDetail: any;
  @Input() set appDetail(thatAppDetail: any) {
    this._appDetail = thatAppDetail;
  }

  get appDetail() {
    return this._appDetail;
  }

  private _activeSession: any;
  @Input() set activeSession(thatActiveSession: any) {
    this._activeSession = thatActiveSession;
  }
  get activeSession(): any {
    return this._activeSession;
  }

  @Output() readonly formClose = new EventEmitter<void>();
  @Output() readonly formSubmit = new EventEmitter<{
    answers?: Record<string, unknown>;
    skippedAll?: string;
    skippedAnswers?: string[];
    otherAnswers?: Record<string, string[]>;
  }>();

  questions: SessionQuestion[] = [];
  config?: any;
  cardHeader: any = '';

  readonly SESSION_MERGE_STATUS = SESSION_MERGE_STATUS;

  isConfirmingClose = false;
  isCollapsed = false;
  skippedAllReason: string;
  currentIndex = 0;
  answers: Record<string, unknown> = {};
  pendingOtherText: Record<string, string> = {};
  customOptions: Record<string, string[]> = {};
  editingCustomOption: Record<string, boolean> = {};

  get currentQuestion(): SessionQuestion | undefined {
    return this.questions[this.currentIndex];
  }

  toggleCollapse(): void {
    this.isCollapsed = !this.isCollapsed;
  }

  get totalCount(): number {
    return this.questions.length;
  }

  hasAnsweredCurrent(): boolean {
    if (!this.currentQuestion) {
      return false;
    }
    const hasPendingText = (this.pendingOtherText[this.currentQuestion.id] || '').trim().length > 0;
    return hasPendingText || this.hasAnsweredQuestion(this.currentQuestion);
  }

  hasAnsweredQuestion(q: SessionQuestion): boolean {
    const ans = this.answers[q.id];

    if (q.type === 'chips' || q.type === 'check-boxes') {
      return Array.isArray(ans) && ans.length > 0;
    }

    if (q.type === 'radio-buttons') {
      return ans !== null && ans !== undefined && ans !== '';
    }

    if (q.type === 'yes-no') {
      const obj = ans as Record<string, string>;
      return obj ? Object.keys(obj).length > 0 : false;
    }
    return ans !== null && ans !== undefined && ans !== '';
  }

  ngOnInit(): void {
    this.initializeAnswers();
  }

  ngDoCheck(): void {
    // Because Firebase/SSE often deeply mutates the chatMessage nested properties 
    // without triggering Angular's @Input object reference switch, we force the 
    // DOM to scan for string property changes (like the labels magically appearing)
    this.cdr.markForCheck();
  }

  initializeAnswers(): void {
    // Completely recreate the answers object to ensure no residual data from old questions exists
    const currentAnswers: Record<string, unknown> = {};
    
    this.questions.forEach((q) => {
      if (q.type === 'check-boxes' || q.type === 'chips') {
        currentAnswers[q.id] = [] as string[];
      } else if (q.type === 'yes-no') {
        currentAnswers[q.id] = {};
      } else {
        currentAnswers[q.id] = null;
      }
    });

    this.answers = currentAnswers;
  }

  setCardHeader() {
    this.cardHeader = this.config && this.config.header;

    if (!this.cardHeader) {
      if (this.chatMessage.messageType === ChatMessageType.RECOMMENDATION) {
        this.cardHeader = "What's Next ?";
      } else {
        if (this.questions.length == 1) {
          this.cardHeader = 'Question';
        } else {
          this.cardHeader = 'Questions';
        }
      }
    }
  }

  private processPendingOtherText(): void {
    const q = this.currentQuestion;
    if (q && this.pendingOtherText[q.id]?.trim().length > 0) {
      if (q.type === 'check-boxes' || q.type === 'radio-buttons' || q.type === 'chips') {
        this.addCustomOption(q.id, q.type as 'radio-buttons' | 'check-boxes' | 'chips');
      }
    }
  }

  private clearPendingOtherText(): void {
    const q = this.currentQuestion;
    if (q && this.pendingOtherText[q.id]) {
      this.pendingOtherText[q.id] = '';
    }
  }

  next(): void {
    this.processPendingOtherText();
    if (this.currentIndex < this.totalCount - 1) {
      this.currentIndex++;
      this.cdr.detectChanges(); // Force DOM update for new question options
    }
  }

  back(): void {
    this.clearPendingOtherText();
    if (this.currentIndex > 0) {
      this.currentIndex--;
      this.cdr.detectChanges(); // Force DOM update for previous question options
    }
  }

  skip(): void {
    this.next();
  }

  resetCardState(): void {
    this.isCollapsed = false;
    this.isConfirmingClose = false;
    this.currentIndex = 0;
    this.customOptions = {};
    this.editingCustomOption = {};
    this.pendingOtherText = {};
    this.answers = {}; // strictly clear reference map
    this.cdr.detectChanges();
  }

  onCloseClick(): void {
    if (this.chatMessage.messageType === ChatMessageType.RECOMMENDATION) {
      // CLOSE-action just hide card.. Not, trigger skipAll flow
      this.closeForm();
    } else if (this.activeSession.isOwner && this.appDetail.mergeStatus !== SESSION_MERGE_STATUS.IN_PROGRESS) {
      if (this.totalCount > 1) {
        this.isConfirmingClose = true;
      } else {
        this.submitForm(true);
      }
    } else {
      this.closeForm();
    }
  }

  closeForm(): void {
    this.formClose.emit();
    setTimeout(() => {
      this.resetCardState();
    }, 350);
  }

  autoGrowTextarea(event: Event): void {
    const textarea = event.target as HTMLTextAreaElement;
    if (textarea) {
      textarea.style.height = 'auto';
      textarea.style.height = Math.min(textarea.scrollHeight, 170) + 'px';
    }
  }

  submitForm(skippedAll?: boolean): void {
    this.processPendingOtherText();
    if (this.activeSession.isOwner && this.appDetail.mergeStatus !== SESSION_MERGE_STATUS.IN_PROGRESS) {
      let rawAnswersClone: Record<string, unknown> = {};

      // Deep clone the raw answers object so mutations don't leak, maintaining original structure for the backend
      rawAnswersClone = JSON.parse(JSON.stringify(this.answers)) as Record<string, unknown>;

      const skippedAnswers: string[] = [];
      const otherAnswers: Record<string, string[]> = {};

      this.questions.forEach((q) => {
        if (!this.hasAnsweredQuestion(q)) {
          skippedAnswers.push(q.id);
          delete rawAnswersClone[q.id]; // Remove skipped/null items from final answers payload
        } else {
          // Extract custom choices from answers
          const val = rawAnswersClone[q.id];
          const customSelected: string[] = [];

          if (Array.isArray(val)) {
            const standardAnswers: unknown[] = [];
            val.forEach((ans) => {
              if (typeof ans === 'string' && ans.startsWith('_custom_')) {
                customSelected.push(ans.replace('_custom_', ''));
              } else {
                standardAnswers.push(ans);
              }
            });

            if (standardAnswers.length > 0) {
              rawAnswersClone[q.id] = standardAnswers;
            } else {
              delete rawAnswersClone[q.id]; // Everything was custom
            }
          } else if (typeof val === 'string' && val.startsWith('_custom_')) {
            customSelected.push(val.replace('_custom_', ''));
            delete rawAnswersClone[q.id];
          }

          if (customSelected.length > 0) {
            otherAnswers[q.id] = customSelected;
          }
        }
      });

      const payload: {
        answers?: Record<string, unknown>;
        skippedAll?: string;

        skippedAnswers?: string[];
        otherAnswers?: Record<string, string[]>;
      } = {};
      // skippedAllReason?: string;

      const isManuallySkippedAll = this.questions.length > 0 && skippedAnswers.length === this.questions.length;

      if (Object.keys(rawAnswersClone).length > 0) {
        payload.answers = rawAnswersClone;
      }

      if (isManuallySkippedAll && !this.isConfirmingClose) {
        // it will show-up card. When call coming 2nd time, this boolean-flag's value might've revised. So, it will by-pass this conditional-block
        this.isConfirmingClose = true;
        return;
      }

      if (skippedAll || isManuallySkippedAll) {
        payload.skippedAll = 'true';

        if (this.chatMessage.messageType === ChatMessageType.RECOMMENDATION) {
          this.closeForm();
          return;
        }
      } else if (skippedAnswers.length > 0) {
        payload.skippedAnswers = skippedAnswers;
      }

      /*
      if (this.skippedAllReason) {
        payload.skippedAllReason = this.skippedAllReason;
      }
        */

      if (Object.keys(otherAnswers).length > 0) {
        payload.otherAnswers = otherAnswers;
      }

      this.formSubmit.emit(payload);
    }

    this.closeForm();
  }

  // Helpers for array-based answers
  toggleRadioSelection(qid: string, val: string, event: Event): void {
    if (this.answers[qid] === val) {
      event.preventDefault();
      setTimeout(() => {
        this.answers = {
          ...this.answers,
          [qid]: null
        };
        const target = event.target as HTMLInputElement;
        if (target && target.type === 'radio') {
          target.checked = false;
        }
      });
    }
  }

  toggleSelection(qid: string, option: string): void {
    if (!this.answers[qid] || !Array.isArray(this.answers[qid])) {
      this.answers[qid] = [];
    }
    const current = this.answers[qid] as string[];
    const idx = current.indexOf(option);
    const minusOne = -1;
    let newArr: string[];
    if (idx > minusOne) {
      newArr = current.filter((o) => o !== option);
    } else {
      newArr = [...current, option];
    }

    // Assign a new array reference and new object reference for Change Detection
    this.answers = {
      ...this.answers,
      [qid]: newArr
    };
  }

  isSelected(qid: string, option: string): boolean {
    const current = this.answers[qid] as string[];
    return current?.includes(option);
  }

  clearOther(qid: string): void {
    // If radio button clears selection by selecting a default option
    // It shouldn't necessarily delete the user's custom options they typed out, just untoggle them from answers.
    // So we don't need any complex logic here anymore since radio bindings natively handle standard `answers` map.
  }

  addCustomOption(qid: string, type: 'radio-buttons' | 'check-boxes' | 'chips'): void {
    const text = (this.pendingOtherText[qid] || '').trim();
    if (!text) return;

    if (!this.customOptions[qid]) {
      this.customOptions[qid] = [];
    }

    if (type === 'radio-buttons') {
      // Force single custom option limits for radios
      this.customOptions = {
        ...this.customOptions,
        [qid]: [text]
      };
      this.answers = {
        ...this.answers,
        [qid]: `_custom_${text}`
      };
    } else {
      // Append for checkboxes / chips
      this.customOptions = {
        ...this.customOptions,
        [qid]: [...this.customOptions[qid], text]
      };

      const currentAns = Array.isArray(this.answers[qid]) ? (this.answers[qid] as string[]) : [];
      this.answers = {
        ...this.answers,
        [qid]: [...currentAns, `_custom_${text}`]
      };
    }

    this.pendingOtherText[qid] = ''; // Clear typed text
  }

  trackByIndex(index: number, obj: any): any {
    return index;
  }

  updateCustomOption(
    qid: string,
    index: number,
    event: Event,
    oldText: string,
    type: 'radio-buttons' | 'check-boxes' | 'chips'
  ): void {
    const input = event.target as HTMLInputElement;
    const newText = input.value.trim();

    if (!newText) {
      this.removeCustomOption(qid, index, type);
      return;
    }

    const newCustomOpts = [...this.customOptions[qid]];
    newCustomOpts[index] = newText;
    this.customOptions = {
      ...this.customOptions,
      [qid]: newCustomOpts
    };

    // Repoint selected answers seamlessly during edit
    if (type === 'radio-buttons') {
      if (this.answers[qid] === `_custom_${oldText}`) {
        this.answers = {
          ...this.answers,
          [qid]: `_custom_${newText}`
        };
      }
    } else if (Array.isArray(this.answers[qid])) {
      const currentAns = this.answers[qid] as string[];
      const idx = currentAns.indexOf(`_custom_${oldText}`);
      if (idx > -1) {
        const newAns = [...currentAns];
        newAns[idx] = `_custom_${newText}`;
        this.answers = {
          ...this.answers,
          [qid]: newAns
        };
      }
    }
  }

  startEditCustomOption(qid: string, index: number, event?: Event): void {
    if (event) {
      event.preventDefault();
      event.stopPropagation();
    }
    this.editingCustomOption[`${qid}_${index}`] = true;

    // Focus the input in the next tick
    setTimeout(() => {
      const inputs = document.querySelectorAll('.inline-edit');
      const input = inputs[inputs.length - 1] as HTMLInputElement;
      if (input) {
        input.focus();
      }
    });
  }

  saveCustomOptionEdit(
    qid: string,
    index: number,
    event: Event,
    oldText: string,
    type: 'radio-buttons' | 'check-boxes' | 'chips'
  ): void {
    const input = event.target as HTMLInputElement;
    const newText = input.value.trim();

    this.editingCustomOption = {
      ...this.editingCustomOption,
      [`${qid}_${index}`]: false
    };

    if (!newText || newText === oldText) {
      if (!newText) {
        this.removeCustomOption(qid, index, type);
      }
      return;
    }

    const newCustomOpts = [...this.customOptions[qid]];
    newCustomOpts[index] = newText;
    this.customOptions = {
      ...this.customOptions,
      [qid]: newCustomOpts
    };

    // Repoint selected answers seamlessly during edit
    if (type === 'radio-buttons') {
      if (this.answers[qid] === `_custom_${oldText}`) {
        this.answers = {
          ...this.answers,
          [qid]: `_custom_${newText}`
        };
      }
    } else if (Array.isArray(this.answers[qid])) {
      const currentAns = this.answers[qid] as string[];
      const idx = currentAns.indexOf(`_custom_${oldText}`);
      if (idx > -1) {
        const newAns = [...currentAns];
        newAns.splice(idx, 1, `_custom_${newText}`);
        this.answers = {
          ...this.answers,
          [qid]: newAns
        };
      }
    }
  }

  removeCustomOption(qid: string, index: number, type: 'radio-buttons' | 'check-boxes' | 'chips'): void {
    const textToRemove = this.customOptions[qid][index];
    const newCustomOpts = [...this.customOptions[qid]];
    newCustomOpts.splice(index, 1);
    this.customOptions = {
      ...this.customOptions,
      [qid]: newCustomOpts
    };

    // Unselect from answers
    if (type === 'radio-buttons') {
      if (this.answers[qid] === `_custom_${textToRemove}`) {
        this.answers = {
          ...this.answers,
          [qid]: null
        };
      }
    } else if (Array.isArray(this.answers[qid])) {
      const currentAns = this.answers[qid] as string[];
      const idx = currentAns.indexOf(`_custom_${textToRemove}`);
      if (idx > -1) {
        const newAns = [...currentAns];
        newAns.splice(idx, 1);
        this.answers = {
          ...this.answers,
          [qid]: newAns
        };
      }
    }
  }

  toggleSlider(qid: string, val: string): void {
    if (this.answers[qid] === val) {
      this.answers[qid] = null; // Unselect if already selected
    } else {
      this.answers[qid] = val;
    }
  }

  hasYesNoAnswer(qid: string, itemId: string): boolean {
    const obj = this.answers[qid] as Record<string, string>;
    return !!(obj && obj[itemId] !== undefined && obj[itemId] !== null);
  }

  getYesNoAnswer(qid: string, itemId: string): string | null {
    const obj = this.answers[qid] as Record<string, string>;
    return obj ? obj[itemId] : null;
  }

  setYesNo(qid: string, item: string, value: string): void {
    const obj = { ...(this.answers[qid] as Record<string, string>) };
    if (obj[item] === value) {
      delete obj[item]; // Unselect if already selected
    } else {
      obj[item] = value;
    }
    this.answers = {
      ...this.answers,
      [qid]: obj
    };
  }

  isSliderConfigValid(thatSessionQuestion: SessionQuestion): boolean {
    const config = thatSessionQuestion.config;
    return !!(config && config.startRange && config.endRange && config.interval);
  }

  getSliderOptions(thatSessionQuestion: SessionQuestion): string[] {
    const config = thatSessionQuestion.config;
    // adding strict null check verification so TS compiler doesn't complain about undefined properties below
    if (
      !this.isSliderConfigValid(thatSessionQuestion) ||
      !config ||
      !config.startRange ||
      !config.endRange ||
      !config.interval
    ) {
      return [];
    }

    const start = parseInt(config.startRange, 10);
    const end = parseInt(config.endRange, 10);
    const interval = parseInt(config.interval, 10);

    const options: string[] = [];
    for (let i = start; i <= end; i += interval) {
      options.push(i.toString());
    }
    return options;
  }
}
